<template>
  <div class="table-editor">
    <script src="https://cdn.jsdelivr.net/npm/hyperformula/dist/hyperformula.full.min.js"></script>

    <table>
      <thead>
        <tr>
          <th></th>
          <th
            v-for="(h, index) in table.headers"
            :key="'header-' + index"
            :class="{ 'is-bold': isColumnBold(index) }"
            :style="'background-color:' + h.background + '; width:' + (h.size || 'auto')">
            <v-text-field
              :label="h.label ? '' : $t('bloquesEditTableblockeditor.alphabet', { index: index + 1 })"
              v-model="h.label"
              hide-details="auto">
            </v-text-field>

            <v-menu v-if="ui.mode == 'full'" transition="slide-y-transition" bottom :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <div class="btn-actions" v-bind="attrs" v-on="on">
                  <v-icon small>mdi-menu-down</v-icon>
                </div>
              </template>
              <v-list class="pb-0" @click.stop>
                <v-list-item
                  v-for="(action, key) in db.actions.column"
                  :key="index + '-action-'+key"
                  @click="doAction(key, index)">
                  <v-list-item-subtitle>{{ $t('bloquesEditTableblockeditor.' + action.label) }}</v-list-item-subtitle>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item @click="toggleHeaderProp('font', 'bold', index)">
                  <v-list-item-subtitle>{{ $t('bloquesEditTableblockeditor.bold') }}</v-list-item-subtitle>
                  <v-list-item-icon>
                    <v-icon v-if="table.headers[index] && table.headers[index].font === 'bold'" small>mdi-check</v-icon>
                  </v-list-item-icon>
                </v-list-item>

                <v-list-item @click="toggleHeaderProp('read', true, index)">
                  <v-list-item-subtitle>{{ $t('bloquesEditTableblockeditor.readOnly') }}</v-list-item-subtitle>
                  <v-list-item-icon>
                    <v-icon v-if="table.headers[index] && table.headers[index].read" small>mdi-check</v-icon>
                  </v-list-item-icon>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item>
                  <div class="row">
                    <div class="col" cols="6" style="color: rgba(0, 0, 0, 0.6); font-size: 14px">
                      {{ $t('bloquesEditTableblockeditor.size') }}
                    </div>
                    <div
                      class="col text-center"
                      cols="2"
                      style="color: rgba(0, 0, 0, 0.6); font-size: 14px; cursor: pointer"
                      @click="setCellStyle('size', 'auto', index)">
                      {{ $t('bloquesEditTableblockeditor.auto') }}
                    </div>
                    <div
                      class="col text-center"
                      cols="2"
                      style="color: rgba(0, 0, 0, 0.6); font-size: 14px; cursor: pointer"
                      @click="setCellStyle('size', '70px', index)">
                      {{ $t('bloquesEditTableblockeditor.70px') }}
                    </div>
                    <div
                      class="col text-center"
                      cols="2"
                      style="color: rgba(0, 0, 0, 0.6); font-size: 14px; cursor: pointer"
                      @click="setCellStyle('size', '30px', index)">
                      {{ $t('bloquesEditTableblockeditor.30px') }}
                    </div>
                  </div>
                </v-list-item>

                <v-divider></v-divider>
              </v-list>
              <div class="color-picker">
                <div
                  class="color-option"
                  v-for="(color, key) in db.actions.colors"
                  :key="index + '-color-'+key"
                  :style="'background-color: '+color"
                  @click="setCellStyle('background', color, index)">
                  {{ $t('bloquesEditTableblockeditor.color') }}
                </div>
              </div>
            </v-menu>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, index) in table.rows" :key="'row-' + index">
          <th>
            {{ index + 1 }}
            <v-menu transition="slide-y-transition" bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="btn-actions" v-bind="attrs" v-on="on">
                  <v-icon small>mdi-menu-down</v-icon>
                </div>
              </template>
              <v-list>
                <v-list-item
                  v-for="(action, key) in db.actions.row"
                  :key="index + '-action-'+key"
                  @click="doAction(key, index)">
                  <v-list-item-subtitle>{{ $t('bloquesEditTableblockeditor.' + action.label) }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-menu>
          </th>
          <td
            v-for="(field, i) in row"
            :key="'field-'+i+index"
            :class="cellClasses(index, i)"
            :style="cellStyle(index, i)">
            <span v-if="isFormula(index, i)" style="position: absolute; pointer-events: none;">
              {{ calCellValue(index, i) }}
            </span>

            <v-text-field
              label=""
              v-model="row[i]"
              :style="cellInputStyle(index, i)"
              :readonly="isCellReadOnly(index, i)"
              :disabled="isCellReadOnly(index, i)"
              :tabindex="isCellReadOnly(index, i) ? -1 : 0"
              hide-details="auto"
              @blur="parseData"
              v-on:keyup.enter="parseData">
            </v-text-field>

            <v-menu v-if="ui.mode == 'full'" transition="slide-y-transition" bottom :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <div class="btn-actions cell-config-actions" v-bind="attrs" v-on="on">
                  <v-icon small>mdi-tune</v-icon>
                </div>
              </template>
              <v-list class="pb-0">
                <v-list-item @click.stop="toggleCellConfig(index, i, 'bold', true)">
                  <v-list-item-subtitle>{{ $t('bloquesEditTableblockeditor.bold') }}</v-list-item-subtitle>
                  <v-list-item-icon>
                    <v-icon v-if="getCellConfig(index, i).bold" small>mdi-check</v-icon>
                  </v-list-item-icon>
                </v-list-item>

                <v-list-item @click.stop="toggleCellConfig(index, i, 'disabled', true)">
                  <v-list-item-subtitle>{{ $t('bloquesEditTableblockeditor.readOnly') }}</v-list-item-subtitle>
                  <v-list-item-icon>
                    <v-icon v-if="getCellConfig(index, i).disabled" small>mdi-check</v-icon>
                  </v-list-item-icon>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item>
                  <div class="row">
                    <div class="col" style="color: rgba(0, 0, 0, 0.6); font-size: 14px">
                      {{ $t('bloquesEditTableblockeditor.border') }}
                    </div>
                    <div
                      class="col text-center"
                      style="color: rgba(0, 0, 0, 0.6); font-size: 14px; cursor: pointer"
                      @click.stop="setCellConfig(index, i, 'border', '1px solid #222')">
                      {{ $t('bloquesEditTableblockeditor.solid') }}
                    </div>
                    <div
                      class="col text-center"
                      style="color: rgba(0, 0, 0, 0.6); font-size: 14px; cursor: pointer"
                      @click.stop="setCellConfig(index, i, 'border', '1px dashed #222')">
                      {{ $t('bloquesEditTableblockeditor.dashed') }}
                    </div>
                    <div
                      class="col text-center"
                      style="color: rgba(0, 0, 0, 0.6); font-size: 14px; cursor: pointer"
                      @click.stop="removeCellConfig(index, i, 'border')">
                      {{ $t('bloquesEditTableblockeditor.none') }}
                    </div>
                  </div>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item @click.stop="clearCellConfig(index, i)">
                  <v-list-item-subtitle>{{ $t('bloquesEditTableblockeditor.clearCellConfig') }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
              <v-card flat class="cell-color-config" @click.stop>
                <v-card-text class="py-2">
                  <div class="cell-color-section">
                    <div class="cell-color-title">{{ $t('bloquesEditTableblockeditor.background') }}</div>
                    <v-color-picker
                      :value="getPickerColor(index, i, 'background')"
                      mode="hex"
                      hide-inputs
                      hide-mode-switch
                      dot-size="18"
                      swatches-max-height="120"
                      @input="setCellConfig(index, i, 'background', normalizeColor($event))">
                    </v-color-picker>
                    <div class="cell-color-value-row">
                      <v-text-field
                        dense
                        outlined
                        hide-details
                        type="number"
                        min="0"
                        max="100"
                        suffix="%"
                        class="cell-opacity-input"
                        :label="$t('bloquesEditTableblockeditor.opacity')"
                        :value="getCellOpacity(index, i, 'background')"
                        @input="setCellOpacity(index, i, 'background', $event)">
                      </v-text-field>
                      <div class="cell-color-preview" :style="colorPreviewStyle(index, i, 'background')"></div>
                      <v-text-field
                        dense
                        outlined
                        hide-details
                        class="cell-color-code-input"
                        :value="getCellColor(index, i, 'background')"
                        @change="setCellColorInput(index, i, 'background', $event)">
                        <template v-slot:append>
                          <v-btn icon x-small class="cell-color-action" @click.stop="copyColor(getCellColor(index, i, 'background'))">
                            <v-icon small>mdi-content-copy</v-icon>
                          </v-btn>
                          <v-btn icon x-small class="cell-color-action" @click.stop="clearCellColor(index, i, 'background')">
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </div>
                  </div>

                  <v-divider class="my-3"></v-divider>

                  <div class="cell-color-section">
                    <div class="cell-color-title">{{ $t('bloquesEditTableblockeditor.textColor') }}</div>
                    <v-color-picker
                      :value="getPickerColor(index, i, 'textColor')"
                      mode="hex"
                      hide-inputs
                      hide-mode-switch
                      dot-size="18"
                      swatches-max-height="120"
                      @input="setCellConfig(index, i, 'textColor', normalizeColor($event))">
                    </v-color-picker>
                    <div class="cell-color-value-row">
                      <v-text-field
                        dense
                        outlined
                        hide-details
                        type="number"
                        min="0"
                        max="100"
                        suffix="%"
                        class="cell-opacity-input"
                        :label="$t('bloquesEditTableblockeditor.opacity')"
                        :value="getCellOpacity(index, i, 'textColor')"
                        @input="setCellOpacity(index, i, 'textColor', $event)">
                      </v-text-field>
                      <div class="cell-color-preview" :style="colorPreviewStyle(index, i, 'textColor')"></div>
                      <v-text-field
                        dense
                        outlined
                        hide-details
                        class="cell-color-code-input"
                        :value="getCellColor(index, i, 'textColor')"
                        @change="setCellColorInput(index, i, 'textColor', $event)">
                        <template v-slot:append>
                          <v-btn icon x-small class="cell-color-action" @click.stop="copyColor(getCellColor(index, i, 'textColor'))">
                            <v-icon small>mdi-content-copy</v-icon>
                          </v-btn>
                          <v-btn icon x-small class="cell-color-action" @click.stop="clearCellColor(index, i, 'textColor')">
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-menu>

            <v-icon small color="gray" class="formula-icon d-none">mdi-function</v-icon>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr @click="addRowAtIndex('bottom')">
          <th></th>
          <td colspan="99" style="padding: 7px;
            text-align: center;
            cursor: pointer;
            background-color: rgb(236, 245, 240);
            text-transform: uppercase;
            font-weight: bold;
            font-size: 11px;
            color: #717171;
            user-select: none;
            border: 1px dashed #ccc !important;">
            {{ $t('bloquesEditTableblockeditor.newRow') }}
          </td>
        </tr>
        <tr>
          <th></th>
          <td v-for="(f, index) in table.footers" :key="'footer-' + index">
            <template v-if="!table.headers[index].size || table.headers[index].size == 'auto'">
              <span style="text-transform: uppercase; padding-right: 5px;">
                {{ f.formula }}
              </span>
              <strong>{{ calcFormula(f.formula, null, index) }}</strong>
              <v-menu top transition="slide-y-transition">
                <template v-slot:activator="{ on, attrs }">
                  <div class="btn-actions" v-bind="attrs" v-on="on">
                    <v-icon small>mdi-menu-down</v-icon>
                  </div>
                </template>
                <v-list>
                  <v-list-item
                    v-for="(action, key) in db.actions.cell"
                    :key="index + '-action-'+key"
                    @click="doAction(key, index)">
                    <v-list-item-subtitle>{{ $t('bloquesEditTableblockeditor.' + action.label) }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>


<script>
/**
 * @project: certiapp-nuxt
 * @file:    pages/bloques/edit/tableBlockEditor.vue
 * @desc:    ...
 * -------------------------------------------
 * Created Date: 21st June 2021
 * Last Modified: Mon May 09 2022
 **/

import { uuid } from 'vue-uuid'
import { HyperFormula } from 'hyperformula'

import notifications from '~/util/notificationHelper'

export default {
  props: ['_ui', '_table'],

  data: () => ({
    hf: null,
    table: {
      rows: [
        ['', ''],
        ['', ''],
        ['', ''],
      ],
      headers: [{ label: '' }, { label: '' }],
      footers: [{ formula: 'count' }, { formula: 'sum' }],
      cellConfig: {},
    },

    db: {
      alcances: [],
      alphabet: [],

      actions: {
        column: {
          'add-right': {
            label: 'Insertar a la derecha',
          },
          'add-left': {
            label: 'Insertar a la izquierda',
          },
          'remove-col': {
            label: 'Eliminar columna',
          },
        },

        row: {
          'add-top': {
            label: 'Insertar 1 encima',
          },
          'add-bottom': {
            label: 'Insertar 1 debajo',
          },
          'remove-row': {
            label: 'Eliminar fila',
          },
        },

        cell: {
          'formula-sum': {
            label: 'Sumar columna - SUM',
          },
          'formula-count': {
            label: 'Contar columnas - COUNT',
          },
          'formula-average': {
            label: 'Media de columna - AVERAGE',
          },
        },

        colors: [
          '#f0f0f0',
          'antiquewhite',
          'aquamarine',
          'cornflowerblue',
          '#c32c2c',
          '#2cc365',
          '#c3b42c',
        ],
      },

      defaults: {
        row: [],
        header: { label: '' },
        footer: { formula: 'count' },
      },
    },

    ui: {
      mode: 'full',
      loading: false,
      isValid: false,
    },
  }),

  computed: {
    // sheet() {
    //   if (this.hf) return this.hf.getAllSheetsValues().Sheet1
    //   return this.table.rows
    // },
  },

  methods: {
    //+-------------------------------------------------
    // isFormula()
    // Returns true when the expression is a formula
    // -----
    // Created on Thu Jul 01 2021
    //+-------------------------------------------------
    isFormula(row, col) {
      let cell = this.table.rows[row][col]

      if (cell && cell.charAt(0) == '=') return true
      return false
    },

    isColumnBold(index) {
      let header = this.table.headers[index]
      if (!header) return false
      return header.font === 'bold'
    },

    isColumnReadOnly(index) {
      let header = this.table.headers[index]
      if (!header) return false
      return Boolean(header.read)
    },

    cellKey(row, col) {
      return row + ':' + col
    },

    getCellConfig(row, col) {
      return this.table.cellConfig?.[this.cellKey(row, col)] || {}
    },

    hasCellConfig(row, col) {
      return Object.keys(this.getCellConfig(row, col)).length > 0
    },

    isCellBold(row, col) {
      let config = this.getCellConfig(row, col)
      if (Object.prototype.hasOwnProperty.call(config, 'bold')) return Boolean(config.bold)
      return this.isColumnBold(col)
    },

    isCellReadOnly(row, col) {
      let config = this.getCellConfig(row, col)
      if (Object.prototype.hasOwnProperty.call(config, 'disabled')) return Boolean(config.disabled)
      return this.isColumnReadOnly(col)
    },

    cellClasses(row, col) {
      return {
        isFormula: this.isFormula(row, col),
        'is-bold': this.isCellBold(row, col),
        'is-readonly': this.isCellReadOnly(row, col),
        'has-cell-config': this.hasCellConfig(row, col),
      }
    },

    cellStyle(row, col) {
      let config = this.getCellConfig(row, col)
      let header = this.table.headers[col] || {}
      let style = {}

      if (config.background || header.background) style.backgroundColor = this.colorWithOpacity(
        config.background || header.background,
        config.background ? config.backgroundOpacity : 100
      )
      if (config.textColor) style.color = this.colorWithOpacity(config.textColor, config.textColorOpacity)
      if (config.border) style.border = config.border

      return style
    },

    cellInputStyle(row, col) {
      let config = this.getCellConfig(row, col)
      let style = {}

      if (config.textColor) style.color = this.colorWithOpacity(config.textColor, config.textColorOpacity)

      return style
    },

    normalizeColor(color) {
      if (!color) return ''
      let normalized = ''
      if (typeof color === 'string') normalized = color
      if (color.hexa) normalized = color.hexa
      if (color.hex) normalized = color.hex
      if (normalized.length === 9) normalized = normalized.substring(0, 7)
      return normalized
    },

    opacityKey(key) {
      return key + 'Opacity'
    },

    getPickerColor(row, col, key) {
      return this.getCellColor(row, col, key) || '#000000'
    },

    colorPreviewStyle(row, col, key) {
      return {
        background: this.colorWithOpacity(
          this.getCellColor(row, col, key) || '#000000',
          this.getCellOpacity(row, col, key)
        ),
      }
    },

    getCellOpacity(row, col, key) {
      let opacity = this.getCellConfig(row, col)[this.opacityKey(key)]
      return opacity === undefined ? 100 : opacity
    },

    setCellOpacity(row, col, key, value) {
      let opacity = parseInt(value, 10)
      if (Number.isNaN(opacity)) opacity = 100
      opacity = Math.min(100, Math.max(0, opacity))
      this.setCellConfig(row, col, this.opacityKey(key), opacity)
    },

    clearCellColor(row, col, key) {
      this.removeCellConfig(row, col, key)
      this.removeCellConfig(row, col, this.opacityKey(key))
    },

    setCellColorInput(row, col, key, value) {
      let color = (value || '').trim()
      if (!color) {
        this.clearCellColor(row, col, key)
        return
      }

      if (!color.startsWith('#')) color = '#' + color
      if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
        this.setCellConfig(row, col, key, color.toUpperCase())
      }
    },

    colorWithOpacity(color, opacity = 100) {
      if (!color) return ''
      if (!color.startsWith('#')) return color
      let hex = color.replace('#', '')
      if (hex.length === 8) hex = hex.substring(0, 6)
      if (hex.length !== 6) return color

      let alpha = Math.min(100, Math.max(0, parseInt(opacity, 10))) / 100
      let red = parseInt(hex.substring(0, 2), 16)
      let green = parseInt(hex.substring(2, 4), 16)
      let blue = parseInt(hex.substring(4, 6), 16)
      return `rgba(${red}, ${green}, ${blue}, ${alpha})`
    },

    getCellColor(row, col, key) {
      let color = this.getCellConfig(row, col)[key] || ''
      if (color.length === 9) color = color.substring(0, 7)
      return color
    },

    async copyColor(color) {
      if (!color) return

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(color)
        } else {
          let input = document.createElement('input')
          input.value = color
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }

        this.$store.commit('notification/show', {
          text: this.$t('bloquesEditTableblockeditor.colorCopied'),
          color: 'success',
          timeout: 1500,
        })
      } catch (e) {
        this.$store.commit('notification/show', {
          text: this.$t('bloquesEditTableblockeditor.colorCopyError'),
          color: 'warning',
          timeout: 1500,
        })
      }
    },

    setCellConfig(row, col, key, value) {
      if (!this.table.cellConfig || Array.isArray(this.table.cellConfig)) {
        this.$set(this.table, 'cellConfig', { ...this.table.cellConfig })
      }

      let cellKey = this.cellKey(row, col)
      let config = { ...this.getCellConfig(row, col), [key]: value }
      this.$set(this.table.cellConfig, cellKey, config)
      this.$forceUpdate()
    },

    removeCellConfig(row, col, key) {
      if (!this.table.cellConfig || Array.isArray(this.table.cellConfig)) {
        this.$set(this.table, 'cellConfig', { ...this.table.cellConfig })
      }

      let cellKey = this.cellKey(row, col)
      let config = { ...this.getCellConfig(row, col) }
      this.$delete(config, key)

      if (Object.keys(config).length) {
        this.$set(this.table.cellConfig, cellKey, config)
      } else {
        this.$delete(this.table.cellConfig, cellKey)
      }

      this.$forceUpdate()
    },

    toggleCellConfig(row, col, key, value) {
      let config = this.getCellConfig(row, col)
      if (config[key] === value) {
        this.removeCellConfig(row, col, key)
      } else {
        this.setCellConfig(row, col, key, value)
      }
    },

    clearCellConfig(row, col) {
      if (!this.table.cellConfig) return
      this.$delete(this.table.cellConfig, this.cellKey(row, col))
      this.$forceUpdate()
    },

    cleanRowsForFormula() {
      return this.table.rows.map((row) => row.map((cell) => cell))
    },

    //+-------------------------------------------------
    // parseData()
    // Gives this.data to hyperformula
    // called to refresh the library
    // -----
    // Created on Wed Jun 30 2021
    //+-------------------------------------------------
    parseData() {
      if (this.hf == null) {
        this.hf = HyperFormula.buildFromArray(this.cleanRowsForFormula(), {
          precisionRounding: 4,
          licenseKey: 'agpl-v3',
        })

        // this.hf.addSheet('footer')
        // this.hf.setSheetContent('footer', this.table.footers)
      } else {
        this.hf.setSheetContent('Sheet1', this.cleanRowsForFormula())
        console.warn('updating content', this.hf.getAllSheetsValues().Sheet1)
        this.$forceUpdate()

        // this.hf.setSheetContent('footer', this.table.footers)
      }
    },

    //+-------------------------------------------------
    // doAction()
    // Performs an action defined by Key
    // -----
    // Created on Mon Jun 28 2021
    //+-------------------------------------------------
    doAction(action, index, secondary) {
      if (action == 'add-right' || action == 'add-left') {
        let position = action == 'add-right' ? index + 1 : index
        this.addColumnAtIndex(position)
      }

      if (action == 'add-top' || action == 'add-bottom') {
        let position = action == 'add-top' ? index : index + 1
        this.addRowAtIndex(position)
      }

      if (action == 'remove-row') {
        this.removeRow(index)
      }

      if (action == 'remove-col') {
        this.removeCol(index)
      }

      if (action == 'formula-sum') {
        this.setFooterFormula('sum', index)
      }

      if (action == 'formula-count') {
        this.setFooterFormula('count', index)
      }

      if (action == 'formula-average') {
        this.setFooterFormula('average', index)
      }

      console.warn('🛠', 'Action performed: ', action)
    },

    //+-------------------------------------------------
    // addColumnAtIndex()
    // Adds a column to headers and each row
    // -----
    // Created on Mon Jun 28 2021
    //+-------------------------------------------------
    addColumnAtIndex(index) {
      let header = { ...this.db.defaults.header }
      let footer = { ...this.db.defaults.footer }

      this.table.headers.splice(index, 0, header)
      this.table.footers.splice(index, 0, footer)

      this.table.rows.forEach((row) => {
        row.splice(index, 0, '')
      })
      this.shiftCellConfigForColumn(index, 1)
    },

    //+-------------------------------------------------
    // addRowAtIndex()
    // Adds a new row
    // -----
    // Created on Tue Jun 29 2021
    //+-------------------------------------------------
    addRowAtIndex(index) {
      index = index == 'bottom' ? this.table.rows.length : index

      let newRow = []
      this.table.headers.forEach((item) => {
        newRow.push('')
      })

      this.table.rows.splice(index, 0, newRow)
      this.shiftCellConfigForRow(index, 1)
    },

    removeRow(index, type) {
      this.table.rows.splice(index, 1)
      this.shiftCellConfigForRow(index, -1)
    },

    removeCol(index, type) {
      this.table.rows.forEach((row) => {
        row.splice(index, 1)
      })
      this.table.footers.splice(index, 1)
      this.table.headers.splice(index, 1)
      this.shiftCellConfigForColumn(index, -1)
    },

    shiftCellConfigForRow(index, offset) {
      if (!this.table.cellConfig) return

      let shifted = {}
      Object.keys(this.table.cellConfig).forEach((key) => {
        let [row, col] = key.split(':').map(Number)
        if (offset > 0 && row >= index) row += offset
        if (offset < 0) {
          if (row === index) return
          if (row > index) row += offset
        }
        shifted[this.cellKey(row, col)] = this.table.cellConfig[key]
      })

      this.$set(this.table, 'cellConfig', shifted)
    },

    shiftCellConfigForColumn(index, offset) {
      if (!this.table.cellConfig) return

      let shifted = {}
      Object.keys(this.table.cellConfig).forEach((key) => {
        let [row, col] = key.split(':').map(Number)
        if (offset > 0 && col >= index) col += offset
        if (offset < 0) {
          if (col === index) return
          if (col > index) col += offset
        }
        shifted[this.cellKey(row, col)] = this.table.cellConfig[key]
      })

      this.$set(this.table, 'cellConfig', shifted)
    },

    //+-------------------------------------------------
    // calCellValue()
    // Calculates the cell value with the hyperformula library
    // -----
    // Created on Thu Jul 01 2021
    //+-------------------------------------------------
    calCellValue(row, col) {
      let value = this.hf.getCellValue({ col: col, row: row, sheet: 0 })
      return value || '...'
    },

    //+-------------------------------------------------
    // calcFormula()
    // Calculates formulas
    // -----
    // Created on Wed Jun 30 2021
    //+-------------------------------------------------
    calcFormula(formula, row, col) {
      let value = 0
      let sheet = this.hf ? [...this.hf.getAllSheetsValues().Sheet1] : this.table.rows

      // SUM
      // Created on Wed Jun 30 2021
      //+-------------------------------------------------
      if (formula == 'sum') {
        sheet.forEach((row) => {
          let item = row[col]
          if (item) {
            item = item.replace ? item.replace(',', '.') : item
            let parsed = parseFloat(item)
            value += parsed
          }
        })
        value = value.toFixed(2)
      }

      // AVERAGE
      // Created on Wed Jun 30 2021
      //+-------------------------------------------------
      if (formula == 'average') {
        let sum = this.calcFormula('sum', row, col)
        let count = this.calcFormula('count', row, col)
        value = sum / count

        value = value.toFixed(2)
      }

      // COUNT
      // Created on Thu Jul 01 2021
      //+-------------------------------------------------
      if (formula == 'count') {
        value = this.table.rows.length
      }

      return value
    },

    //+-------------------------------------------------
    // setCellStyle()
    // Sets additional values to cell
    // -----
    // Created on Wed Jun 30 2021
    //+-------------------------------------------------
    setCellStyle(style, value, index) {
      this.table.headers[index][style] = value
      this.$forceUpdate()
    },

    //+-------------------------------------------------
    // toggleHeaderProp()
    // Toggles header properties that should be stored
    // in JSON only when active
    // -----
    // Created on Fri Feb 06 2026
    //+-------------------------------------------------
    toggleHeaderProp(prop, value, index) {
      let header = this.table.headers[index]
      if (!header) return

      let isActive = false
      if (prop === 'font') isActive = header.font === value
      if (prop === 'read') isActive = Boolean(header.read)

      if (isActive) {
        this.$delete(header, prop)
      } else {
        this.$set(header, prop, value)
      }

      this.$forceUpdate()
    },

    //+-------------------------------------------------
    // setFooterFormula()
    // Defines a formula in a cell
    // -----
    // Created on Wed Jun 30 2021
    //+-------------------------------------------------
    setFooterFormula(formula, column) {
      this.table.footers[column] = {
        formula: formula,
      }

      this.$forceUpdate()
    },

    //+-------------------------------------------------
    // getDataFromProps()
    // Assigns this.table values from _data
    // -----
    // Created on Tue Jul 06 2021
    //+-------------------------------------------------
    getDataFromProps() {
      if (this._table && this._table.rows) {
        this.table = { ...this._table }
      }
      if (!this.table.cellConfig || Array.isArray(this.table.cellConfig)) {
        this.$set(this.table, 'cellConfig', { ...this.table.cellConfig })
      }
      console.warn(this._table.headers, this.table.headers)

      this.ui.mode = this._ui?.mode || 'limited'
    },

    //+-------------------------------------------------
    // normalizeColsAndRows()
    // Ensures that there is enough elements in each row
    // for every header
    // -----
    // Created on Wed Jun 30 2021
    //+-------------------------------------------------
    normalizeColsAndRows() {
      let headers = this.table.headers.length
      this.table.rows.forEach((row) => {
        if (row.length < headers) {
          for (let i = row.length; i < headers; i++) {
            row.push('')
          }
        }
      })
    },

    createKeyedAlphabet() {
      var a = 97
      var charArray = {}
      for (var i = 0; i < 26; i++) charArray[i + 1] = String.fromCharCode(a + i).toUpperCase()

      this.db.alphabet = charArray
    },

    //+-------------------------------------------------
    // Funciones de flujo
    // Submit, validate, Upload...
    //+-------------------------------------------------

    //+-------------------------------------------------
    // Async API crud actions
    //+-------------------------------------------------

    //+-------------------------------------------------
    // Async API requests
    //+-------------------------------------------------

    async init() {
      this.getDataFromProps()
      this.createKeyedAlphabet()
      this.normalizeColsAndRows()
      this.parseData()
    },
  },

  mounted() {
    this.init()
  },
}
</script>

<style>
.table-editor {
  background: whitesmoke;
  box-shadow: 0px 0px 2px #dedede;
}

.table-editor table {
  width: 100%;
  border-collapse: collapse;
}

.table-editor th,
.table-editor td {
  position: relative;
  border-top-width: 0;
  border-left-width: 0;
  height: 22px;
  empty-cells: show;
  line-height: 21px;
  padding: 0 4px;
  background-color: #fff;
  vertical-align: top;
  overflow: hidden;
  outline-width: 0;
  white-space: pre-wrap;

  border: 1px solid #ccc;
}

.table-editor th {
  background-color: #f0f0f0;
  color: #222;
  text-align: center;
  font-size: 13px;
  font-weight: 400;
  white-space: nowrap;
  height: 26px;
  line-height: 25px;
}

.table-editor tbody th {
  width: 60px;
}

.table-editor td {
  color: #222;
  font-size: 14px;
  line-height: 25px;
  font-weight: 400;
  white-space: nowrap;
}

.table-editor td .v-input input {
  color: inherit;
}

.table-editor th.is-bold,
.table-editor td.is-bold,
.table-editor th.is-bold input,
.table-editor td.is-bold input {
  font-weight: 700;
}

.table-editor tfoot td {
  background-color: #f0f0f0;
  text-align: right;
  padding-right: 30px;
}

td.isFormula {
  background-color: aliceblue;
}

td.isFormula .v-input {
  width: 100%;
  display: inline-block;
  opacity: 0;
}

td.isFormula:hover .v-input {
  opacity: 1;
}

td.isFormula:hover span {
  opacity: 0;
}

td.isFormula .formula-icon {
  font-size: 16px;
  display: inline-block !important;
  transform: translateX(-25px);
}

.table-editor td.is-readonly .v-input,
.table-editor td.is-readonly .v-input input {
  pointer-events: none;
}

.table-editor td.is-readonly .v-input--is-disabled {
  opacity: 1;
}

.table-editor td.is-readonly .v-input--is-disabled input {
  color: #222;
}

.table-editor td.is-readonly .v-input--is-disabled[style] input {
  color: inherit;
}

.table-editor td.has-cell-config {
  outline: 1px dashed rgba(76, 175, 80, 0.45);
  outline-offset: -2px;
}

.table-editor .btn-actions {
  opacity: 0;
  outline: 1px dashed #d8d8d8;
  outline-offset: -1px;
  position: absolute;
  right: 4px;
  top: 5px;
  line-height: 14px;
  padding: 0px;
  transition: opacity 0.3s;
}

.table-editor .cell-config-actions {
  right: 22px;
}

.table-editor th:hover .btn-actions,
.table-editor td:hover .btn-actions {
  opacity: 1;
}

.v-menu__content .color-picker {
  max-width: 240px;
  background: white;
  flex-wrap: wrap;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
}

.v-menu__content .color-option {
  cursor: pointer;
  width: 40px;
  height: 40px;
  padding: 0px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-content: center;
  outline: 2px solid #ccc;
  outline-offset: -2px;
}

.v-menu__content .cell-color-config {
  position: relative;
  width: 320px;
}

.v-menu__content .cell-color-title {
  color: rgba(0, 0, 0, 0.7);
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
}

.v-menu__content .cell-color-config .v-color-picker {
  width: 100%;
  box-shadow: none;
}

.v-menu__content .cell-color-section {
  position: relative;
}

.v-menu__content .cell-color-preview {
  flex: 0 0 42px;
  height: 28px;
  width: 42px;
}

.v-menu__content .cell-color-value-row {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 10px;
}

.v-menu__content .cell-color-code-input {
  flex: 0 0 140px;
  max-width: 140px;
}

.v-menu__content .cell-color-action {
  color: #6b7280;
  height: 20px;
  margin-left: 2px;
  width: 20px;
}

.v-menu__content .cell-color-action:hover {
  color: #111827;
}

.v-menu__content .cell-color-action .v-icon {
  cursor: pointer;
  font-size: 16px;
}

.v-menu__content .cell-opacity-input {
  flex: 0 0 82px;
  max-width: 82px;
}

.v-menu__content .cell-opacity-input input {
  text-align: center;
}

.v-menu__content .cell-color-config .v-color-picker__dot {
  display: none;
}

.v-menu__content .cell-color-config .v-color-picker__sliders {
  padding-right: 0;
}

.v-menu__content .cell-color-config .v-slider__thumb::before,
.v-menu__content .cell-color-config .v-slider__thumb-container--focused .v-slider__thumb::before {
  display: none !important;
  opacity: 0 !important;
}

.v-menu__content .cell-color-config .v-ripple__container,
.v-menu__content .cell-color-config .v-ripple__animation {
  display: none !important;
  opacity: 0 !important;
}

.table-editor .v-input,
.table-editor .v-input .v-label {
  padding: 0;
  margin: 0;
  color: #222;
  font-size: inherit;
}

.table-editor .v-input .v-label {
  transform: translate(0px, -2px);
}

.table-editor input {
  padding: 0;
  margin: 0;
  line-height: unset;
}
</style>
